import axios from 'axios';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { convertIndex } from '../../utils';
import { RecorderStateProps } from '.';
import { getProfileName } from '~/BonjorosList/util';
import { toString, parse, tokenize } from '~/utils/templating';
import type { Thumbnail } from '@vimily/bonjoro-ui-components-v2/VideoRecorder';

export function getRecorderState(state: { recorder: RecorderStateProps }) {
  return state.recorder;
}

export function getBlob(state: RecorderStateProps) {
  return state.blob;
}

export function getVideoSourceUrl(state: RecorderStateProps): string | null {
  return state.videoSourceUrl ?? null;
}

export function getThumbnails(state: RecorderStateProps) {
  return state.thumbs ?? [];
}

export const thumbTotal = 9;

export const getThumbnailCount = createSelector(
  getThumbnails,
  (thumbs) => thumbs.length
);

export function getThumbnailGridIndex(state: RecorderStateProps) {
  return state.thumbIndex ?? 0;
}

export const getThumbnailIndex = createSelector(
  getThumbnailCount,
  getThumbnailGridIndex,
  (count, index) => convertIndex(count, index, thumbTotal)
);

export const getInterpolatedThumbnailIndices = createSelector(
  getThumbnailCount,
  (count) => {
    if (count > 0) {
      const step = count / thumbTotal;

      // we want a 3x3 grid of thumbnails
      return Array(thumbTotal)
        .fill(0)
        .map((_, i) => Math.floor(step * i));
    }

    return [];
  }
);

export const getSelectedThumbnailData = createSelector(
  getThumbnails,
  getThumbnailIndex,
  (thumbs, index): Thumbnail | undefined => thumbs[index]
);

export const getInterpolatedThumbnails = createSelector(
  getThumbnails,
  getInterpolatedThumbnailIndices,
  (thumbs, indices) =>
    indices.map((index) => ({
      thumb: thumbs[index].small,
    }))
);

type ThumbType = keyof Omit<Thumbnail, 'time' | 'hash'>;

export const getThumbnail = (thumbType: ThumbType = 'square') =>
  createSelector(getSelectedThumbnailData, (data) => data?.[thumbType]?.data);

export const getThumbnailBlob = (thumbType: ThumbType = 'square') =>
  createSelector(getThumbnail(thumbType), async (thumb) => {
    if (thumb) {
      const res = await axios.get<Blob>(thumb, {
        responseType: 'blob',
      });

      return res.data;
    }

    return null;
  });

export const getThumbnailTime = createSelector(
  getSelectedThumbnailData,
  (data) => data?.time.toMillis()
);

export function getMessage(state: RecorderStateProps) {
  return state.message;
}

export const isThumbnailSelected = createSelector(
  getThumbnailGridIndex,
  (index) => index != null
);

export const getVideoUrl = createSelector(
  getBlob,
  getVideoSourceUrl,
  (blob, videoSourceUrl) => {
    if (videoSourceUrl) return videoSourceUrl;

    return blob != null ? URL.createObjectURL(blob) : null;
  }
);

export function isExternalVideo(state: RecorderStateProps): boolean {
  return state.isExternalVideo;
}

export function getMessageTemplate(state: RecorderStateProps) {
  return state.messageTemplate;
}

export function isFirstTime(state: RecorderStateProps) {
  return state.isFirstTime;
}

export function getGreet(state: RecorderStateProps) {
  return state.greet;
}

export function hasCustomMessage(state: RecorderStateProps) {
  return state.hasCustomMessage;
}

export function getSendType(state: RecorderStateProps) {
  return state.sendType;
}

export function getScheduledDateTime(
  state: RecorderStateProps
): DateTime | null {
  return state.scheduledDateTime;
}

export function getShowThumbnailSelection(state: RecorderStateProps) {
  return state.showThumbnailSelection;
}

export const getRenderedMessage = createSelector(
  getGreet,
  getMessage,
  (greet, input) => {
    const profile = greet?.profiles?.[0];

    return toString(parse(tokenize(input)), {
      first_name: profile?.first_name,
      last_name: profile?.last_name,
      full_name: profile && getProfileName(profile),
      email: profile?.email,
    });
  }
);
